<script setup lang="ts">
// « Votre formule » dans la colonne verte, en deux groupes :
// - « Inclus dans votre mensualité » : les garanties, coche ronde non cliquable (rien à décocher) ;
// - « En option » : entretien et pièces d'usure (vraie case, sur demande) et, à l'étape 2,
//   la reprise de la voiture du client : cercle pointillé + « Estimer » / « Estimée · 44 500 € »
//   tant qu'elle n'est pas dans l'offre affichée, case carrée cochée « Estimée » quand elle l'est
//   (la pastille ronde reste réservée à « Inclus »).
import { computed } from "vue";
import { TradeIn } from "@/types";
import { useFilters } from "@/helpers/filters";
import CheckStroke from "@/Components/Icons/CheckStroke.vue";

const props = withDefaults(
    defineProps<{
        premiumWarranty: boolean;
        purchaseValueWarranty: boolean;
        /** Ligne « Reprise de votre voiture » (étape 2 seulement) */
        showTradeIn?: boolean;
        tradeIn?: TradeIn | null;
        /** La reprise est utilisée en premier loyer dans l'offre affichée */
        applied?: boolean;
    }>(),
    { showTradeIn: false, tradeIn: null, applied: false },
);

const emit = defineEmits<{
    (e: "open-trade-in"): void;
}>();

const maintenanceRequested = defineModel<boolean>("maintenanceRequested", {
    default: false,
});

const { formatAmount } = useFilters();

const hasIncluded = computed(
    () => props.premiumWarranty || props.purchaseValueWarranty,
);

const tradeInLabel = computed(() => {
    const t = props.tradeIn;
    if (!t) {
        return "Estimer";
    }
    if (t.status === "pending_expert" || t.value === null) {
        return "À estimer";
    }
    return props.applied
        ? "Estimée"
        : `Estimée · ${formatAmount(t.value)}\u00a0€`;
});
</script>

<template>
    <div class="formula" data-testid="simulation-formula">
        <p class="formula__title">Votre formule</p>

        <template v-if="hasIncluded">
            <p id="formula-included" class="formula__group">
                Inclus dans votre mensualité
            </p>
            <ul class="formula__list" aria-labelledby="formula-included">
                <li v-if="premiumWarranty" class="formula__row">
                    <span class="formula__dot" aria-hidden="true">
                        <CheckStroke :stroke-width="3.4" />
                    </span>
                    <span>Garantie premium étendue</span>
                    <span class="formula__tag">Incluse</span>
                </li>
                <li v-if="purchaseValueWarranty" class="formula__row">
                    <span class="formula__dot" aria-hidden="true">
                        <CheckStroke :stroke-width="3.4" />
                    </span>
                    <span>Garantie valeur d'achat</span>
                    <span class="formula__tag">Incluse</span>
                </li>
            </ul>
        </template>

        <p id="formula-options" class="formula__group">En option</p>
        <ul class="formula__list" aria-labelledby="formula-options">
            <li>
                <label class="formula__row formula__option">
                    <input
                        v-model="maintenanceRequested"
                        type="checkbox"
                        class="formula__input"
                        data-testid="maintenance-requested"
                    />
                    <span class="formula__box" aria-hidden="true">
                        <CheckStroke :stroke-width="3.4" />
                    </span>
                    <span>Entretien et pièces d'usure</span>
                    <span class="formula__tag formula__tag--muted"
                        >Sur demande</span
                    >
                </label>
            </li>
            <li
                v-if="showTradeIn"
                class="formula__row"
                data-testid="formula-trade-in"
            >
                <span
                    v-if="applied"
                    class="formula__box formula__box--checked"
                    aria-hidden="true"
                >
                    <CheckStroke :stroke-width="3.4" />
                </span>
                <span v-else class="formula__ring" aria-hidden="true"></span>
                <span>Reprise de votre voiture</span>
                <button
                    type="button"
                    class="formula__add"
                    :class="{ 'formula__add--applied': applied }"
                    :aria-label="
                        tradeIn
                            ? `Reprise de votre voiture : ${tradeInLabel}, modifier`
                            : 'Estimer la reprise de votre voiture'
                    "
                    @click="emit('open-trade-in')"
                >
                    {{ tradeInLabel }}
                </button>
            </li>
        </ul>
    </div>
</template>

<style lang="scss" scoped>
.formula {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    color: white;

    &__title {
        margin: 0 0 0.5rem;
        font-family: $bebas;
        font-size: 1.125rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    &__group {
        margin: 0.5rem 0 0.375rem;
        font-size: 0.6875rem;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.6);
    }

    &__list {
        display: grid;
        gap: 0.5rem;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__row {
        display: grid;
        grid-template-columns: 20px 1fr auto;
        gap: 0.75rem;
        align-items: center;
        min-height: 24px;
        font-size: 0.9375rem;
    }

    &__option {
        position: relative;
        cursor: pointer;
    }

    &__input {
        position: absolute;
        opacity: 0;
        width: 1px;
        height: 1px;
    }

    // Inclus : pastille ronde pleine, rien à cocher
    &__dot {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: $primary;
        color: $dark-font;
    }

    // Option non prise : cercle pointillé
    &__ring {
        width: 20px;
        height: 20px;
        box-sizing: border-box;
        border-radius: 50%;
        border: 1.5px dashed rgba(255, 255, 255, 0.55);
    }

    // Option à cocher : vraie case carrée
    &__box {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        box-sizing: border-box;
        border-radius: 4px;
        border: 1.5px solid rgba(255, 255, 255, 0.6);
        color: transparent;
    }

    &__box--checked,
    &__input:checked + &__box {
        border-color: $primary;
        background: $primary;
        color: $dark-font;
    }

    &__input:focus-visible + &__box {
        outline: 2px solid $primary;
        outline-offset: 2px;
    }

    &__tag {
        font-size: 0.75rem;
        white-space: nowrap;
        color: rgba(255, 255, 255, 0.75);

        &--muted {
            color: rgba(255, 255, 255, 0.65);
        }
    }

    &__add {
        position: relative;
        min-height: 24px;
        padding: 2px 10px;
        border: 1px solid rgba(255, 255, 255, 0.45);
        border-radius: 50px;
        background: none;
        color: white;
        font: 500 0.75rem $roboto;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
        cursor: pointer;
        transition:
            background-color 0.2s ease,
            border-color 0.2s ease;

        &:hover {
            border-color: white;
            background: rgba(255, 255, 255, 0.08);
        }

        &:focus-visible {
            outline: 2px solid $primary;
            outline-offset: 2px;
        }

        // Zone de clic de 44 px de haut
        &::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            top: 50%;
            height: 44px;
            transform: translateY(-50%);
        }

        // Dans l'offre : « Estimée » comme « Incluse », sans pastille
        &--applied {
            border-color: transparent;
            padding-right: 0;
            color: $primary;
            text-decoration: underline;
            text-decoration-color: rgba($primary, 0.4);
            text-underline-offset: 3px;

            &:hover {
                border-color: transparent;
                background: none;
            }
        }
    }

    // Ordinateur peu haut : la liste tient avec la calculette sans faire défiler la colonne
    @media (min-width: 1024px) and (max-height: 900px) {
        margin-top: 1rem;
        padding-top: 1rem;

        &__list {
            gap: 0.375rem;
        }
    }
}
</style>
